
export const method = {
  hero: {
    label: 'Technical Process',
    title: 'FGT Method',
    subtitle: 'Strategy without execution is theory. Execution without method is risk.',
    description: '5-step technical process for wealth structuring via regulated consortia — from analysis to monitoring.',
    stats: [
      { value: '5', label: 'Structured steps' },
      { value: '100%', label: 'Regulation-based' },
      { value: '1 business day', label: 'Diagnosis return' }
    ]
  },
  philosophy: {
    label: 'Foundations',
    title: 'A technical approach, not a commercial one',
    paragraph1: 'The FGT Method was developed to eliminate subjectivity from wealth decisions. Each step is documented, each variable is mapped, and each scenario is modeled before any recommendation.',
    paragraph2: 'We do not work with intuition or promises. We work with technical analysis, regulations, and real data from active groups.',
    pillars: [
      {
        icon: 'ri-search-eye-line',
        title: 'Diagnosis before solution',
        text: 'No structure is proposed without a complete mapping of the client\'s wealth situation, objectives, and restrictions.'
      },
      {
        icon: 'ri-bar-chart-grouped-line',
        title: 'Scenario modeling',
        text: 'We build base and stress scenarios for each structure, with cost projections, timelines, and operational risks.'
      },
      {
        icon: 'ri-shield-check-line',
        title: 'Regulatory compliance',
        text: 'All analysis is based on the current administrator\'s regulations and the rules of the Central Bank of Brazil.'
      },
      {
        icon: 'ri-route-line',
        title: 'Continuous monitoring',
        text: 'The process does not end at adhesion. We monitor assemblies, bid opportunities, and support until credit utilization.'
      }
    ]
  },
  steps: [
    {
      number: '01',
      title: 'Wealth Diagnosis',
      description: 'Complete mapping of current situation: objectives, payment capacity, time horizon, risk profile, and operational restrictions.',
      details: [
        'Personal or corporate cash flow analysis',
        'Identification of priority wealth objectives',
        'Monthly payment capacity assessment',
        'Planning horizon definition',
        'Survey of documentary and operational restrictions'
      ],
      duration: '1 meeting of 60–90 min',
      deliverable: 'Wealth diagnosis report with objectives and capacity mapping'
    },
    {
      number: '02',
      title: 'Group and Regulation Analysis',
      description: 'Technical study of active groups, administrators, fees, reserve funds, contemplation dynamics, and operational rules.',
      details: [
        'Comparison of administrators and current regulations',
        'Analysis of administration fees and reserve funds',
        'Study of contemplation history and dynamics',
        'Validation of restrictions and documentary requirements',
        'Assessment of administrator solidity and reputation'
      ],
      duration: '2–3 business days',
      deliverable: 'Comparative group analysis with grounded technical recommendation'
    },
    {
      number: '03',
      title: 'Scenario Modeling',
      description: 'Construction of base and stress scenarios: timeline projection, total costs, bid impact, risk and expected return analysis.',
      details: [
        'Base scenario: contemplation by draw without bid',
        'Stress scenario: contemplation by bid at different percentages',
        'Total cost projection (fees + funds + correction)',
        'Sensitivity and operational risk analysis',
        'Comparison with conventional credit alternatives'
      ],
      duration: '2–4 business days',
      deliverable: 'Modeling document with 3 scenarios, projections, and risk analysis'
    },
    {
      number: '04',
      title: 'Execution Structure',
      description: 'Entry strategy definition, group selection, bid structuring (when applicable), document preparation, and formalization.',
      details: [
        'Final selection of group and administrator',
        'Bid contemplation strategy structuring',
        'Checklist and preparation of necessary documentation',
        'Formalization and adhesion to selected group',
        'Complete briefing on member obligations and rights'
      ],
      duration: '3–7 business days',
      deliverable: 'Formalized adhesion contract and member obligations guide'
    },
    {
      number: '05',
      title: 'Monitoring until Utilization',
      description: 'Continuous management: assembly monitoring, bid opportunity analysis, support in contemplation and credit utilization.',
      details: [
        'Monthly assembly and results monitoring',
        'Bid opportunity analysis at each assembly',
        'Full support in contemplation and documentation',
        'Credit release and utilization management',
        'Monitoring until complete operation closure'
      ],
      duration: 'Throughout the group\'s validity',
      deliverable: 'Monthly monitoring reports and dedicated support until utilization'
    }
  ],
  stepsSection: {
    label: 'The 5 Steps',
    title: 'From assessment to credit utilization',
    subtitle: 'Each step has defined deliverables, estimated timeline, and clear responsibilities.',
    durationLabel: 'Estimated timeline',
    deliverableLabel: 'Deliverable',
    activitiesLabel: 'Activities in this step'
  },
  differentials: {
    label: 'Differentials',
    title: 'Why the method matters',
    subtitle: 'The difference between a good decision and an expensive one lies in the quality of the analysis that precedes it.',
    items: [
      {
        icon: 'ri-microscope-line',
        title: 'Independent technical analysis',
        text: 'We are not representatives of administrators. Our analysis is independent and based exclusively on the client\'s interest.'
      },
      {
        icon: 'ri-file-chart-line',
        title: 'Documentation at each step',
        text: 'Each phase generates a documented deliverable: diagnosis, comparative analysis, scenario modeling, and monitoring reports.'
      },
      {
        icon: 'ri-scales-3-line',
        title: 'BACEN compliance',
        text: 'All structures comply with the rules of the Central Bank of Brazil and current regulations of authorized administrators.'
      },
      {
        icon: 'ri-focus-3-line',
        title: 'Focus on objective, not product',
        text: 'We start from the client\'s wealth objective to select the most appropriate structure — not the other way around.'
      },
      {
        icon: 'ri-team-line',
        title: 'Dedicated and continuous support',
        text: 'A dedicated consultant accompanies each operation from start to finish, available for questions and decisions throughout the process.'
      },
      {
        icon: 'ri-eye-line',
        title: 'Full cost transparency',
        text: 'All costs are mapped before adhesion: administration fee, reserve fund, insurance, and any default charges.'
      }
    ]
  },
  governance: {
    label: 'Governance',
    title: 'Transparency as a principle',
    paragraph1: 'The FGT Method operates under strict principles of transparency and regulatory compliance. Each recommendation is grounded in verifiable data and public regulations.',
    disclaimer: 'The FGT method does not guarantee contemplation within a specific timeframe. It does not promise fixed return or determined profitability. All projections depend on administrator regulations, group dynamics, and assembly decisions.',
    items: [
      {
        icon: 'ri-bank-line',
        title: 'Regulated by the Central Bank',
        text: 'Consortia are regulated by BACEN (Law 11.795/2008). We operate exclusively with authorized administrators.'
      },
      {
        icon: 'ri-lock-line',
        title: 'No contemplation promise',
        text: 'No FGT consultant promises a contemplation timeline. We model scenarios — we do not guarantee results.'
      },
      {
        icon: 'ri-file-shield-2-line',
        title: 'Complete documentation',
        text: 'Every operation is documented: diagnosis, analysis, modeling, contract, and monitoring reports.'
      }
    ]
  },
  cta: {
    title: 'Apply the method to your case',
    subtitle: 'Schedule a personalized strategic assessment. Free and no commitment.',
    button: 'Schedule Strategic Assessment',
    secondary: 'Talk on WhatsApp'
  }
};
